/**
 * Derived invoice display status — for account-balance tenants.
 *
 * Backstory: After ripping out applyFIFOSettlement (2026-05-10), invoice
 * `status` columns no longer get chased into sync with the running
 * membership balance. That's correct behavior in account-balance world —
 * `trg_recalculate_membership_balance` is the source of truth, not the
 * per-invoice status — but it means an invoice can stay `outstanding`
 * forever even when the member is paid up via account credits.
 *
 * This helper resolves what to SHOW the user without mutating the
 * underlying status. When the membership has a non-negative balance,
 * outstanding/partially_paid debits render as "Paid (via balance)" so
 * billing-history surfaces match the member's actual financial reality.
 *
 * Pure display — never feed the result back into business logic. The
 * stored status is still the truth for things like "can I edit this?"
 * or "did this go through autopay?" — only the user-facing label changes.
 */

export type DerivedInvoiceStatus =
  | "paid"
  | "outstanding"
  | "partially_paid"
  | "settled_by_balance"
  | "refunded"
  | "draft"
  | "pending"
  | string; // pass-through for unknown statuses (forward-compat)

interface InvoiceLike {
  status?: string | null;
  amount?: number | string | null;
  /**
   * What the family's money has paid on this charge, sent by the backend for
   * clubs that keep an account balance (apps/backend services/accountAllocation,
   * PM 3ebf2943): "paid" | "partially_paid" | "unpaid". When present it
   * decides, whatever the balance: with $1 owing, a family's paid September
   * dues used to read "outstanding" here.
   */
  payment_state?: string | null;
}

const DERIVED_OF_PAYMENT_STATE: Record<string, DerivedInvoiceStatus> = {
  paid: "settled_by_balance",
  partially_paid: "partially_paid",
  unpaid: "outstanding",
};

export function deriveInvoiceDisplayStatus(
  invoice: InvoiceLike,
  membershipBalance: number | string | null | undefined
): DerivedInvoiceStatus {
  const status = (invoice?.status || "").toString();
  const allocated = DERIVED_OF_PAYMENT_STATE[String(invoice?.payment_state || "")];
  if (allocated && (status === "outstanding" || status === "partially_paid" || status === "pending")) {
    return allocated;
  }
  if (status !== "outstanding" && status !== "partially_paid") {
    return status;
  }

  // An UNKNOWN balance is not a zero balance. The Unpaid Invoices list was
  // never sent the family's balance, read it as $0, and showed every unpaid
  // invoice as "Paid (via balance)" in green (2026-10-10). Without a balance,
  // show the stored status.
  if (membershipBalance === null || membershipBalance === undefined || membershipBalance === "") {
    return status;
  }
  const balance =
    typeof membershipBalance === "number"
      ? membershipBalance
      : parseFloat(String(membershipBalance));
  if (!Number.isFinite(balance)) return status;

  // Account-balance world: a non-negative balance means the member's
  // ledger is settled — any individual outstanding line item has
  // effectively been covered by credits applied at the account level.
  if (balance >= 0) return "settled_by_balance";

  return status;
}

/**
 * Human label for a derived invoice display status. Centralized so a
 * tenant locale override (L7-style) could later plug in here.
 */
export function getDerivedStatusLabel(derived: DerivedInvoiceStatus): string {
  switch (derived) {
    case "paid":
      return "Paid";
    case "settled_by_balance":
      return "Paid (via balance)";
    case "outstanding":
      return "Outstanding";
    case "partially_paid":
      return "Partially Paid";
    case "refunded":
      return "Refunded";
    case "draft":
      return "Draft";
    case "pending":
      return "Pending";
    default:
      return String(derived || "—");
  }
}

/**
 * Tailwind badge classes for a derived status. Same color family as the
 * plain status it derives from, with a softer treatment for the
 * settled-by-balance case to signal "this is a derived display, not a
 * status mutation."
 */
export function getDerivedStatusBadgeClass(
  derived: DerivedInvoiceStatus,
  isDarkMode = false
): string {
  switch (derived) {
    case "paid":
      return isDarkMode
        ? "bg-green-900 text-green-200"
        : "bg-green-100 text-green-800";
    case "settled_by_balance":
      return isDarkMode
        ? "bg-green-900/60 text-green-200 italic"
        : "bg-green-50 text-green-700 italic";
    case "outstanding":
      return isDarkMode
        ? "bg-red-900 text-red-200"
        : "bg-red-100 text-red-800";
    case "partially_paid":
      return isDarkMode
        ? "bg-yellow-900 text-yellow-200"
        : "bg-yellow-100 text-yellow-800";
    case "refunded":
      return isDarkMode
        ? "bg-gray-700 text-gray-200"
        : "bg-gray-100 text-gray-700";
    case "draft":
      return isDarkMode
        ? "bg-gray-700 text-gray-300"
        : "bg-gray-100 text-gray-600";
    case "pending":
      return isDarkMode
        ? "bg-blue-900 text-blue-200"
        : "bg-blue-100 text-blue-800";
    default:
      return isDarkMode
        ? "bg-gray-700 text-gray-200"
        : "bg-gray-100 text-gray-700";
  }
}
